import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateChild, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, map, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthSession } from '../models/auth.models';
import { APP_ROUTES } from '../../constants/routes.constants';

/**
 * Authentication Guard
 * Protects routes that require authentication
 *
 * For MVP: Only validates authentication, no roles/permissions
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.checkAuth(state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.checkAuth(state);
  }

  private checkAuth(state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.session$.pipe(
      take(1),
      map(session => {
        // Check if user is authenticated and session is valid
        if (!session?.isAuthenticated) {
          this.redirectToLogin(state.url);
          return false;
        }

        // Additional validation: check if current session token is still valid
        try {
          const currentSession = this.authService.getCurrentSession();
          if (!currentSession || !this.validateCurrentToken(currentSession)) {
            // Clear invalid session and redirect to login
            this.authService.logout().subscribe(() => {
              this.redirectToLogin(state.url, 'Su sesión ha expirado o el token no es válido');
            });
            return false;
          }
        } catch {
          this.authService.logout().subscribe(() => {
            this.redirectToLogin(state.url, 'Error de validación de token');
          });
          return false;
        }

        return true;
      })
    );
  }

  private redirectToLogin(returnUrl: string, message?: string): void {
    const queryParams: any = { returnUrl };
    if (message) {
      queryParams.error = 'invalid_token';
      queryParams.message = message;
    }

    this.router.navigate([APP_ROUTES.AUTH.BASE], { queryParams });
  }

  private validateCurrentToken(session: AuthSession): boolean {
    if (!session.tokens?.accessToken) {
      return false;
    }

    try {
      // Decode and validate JWT token manually
      const parts = session.tokens.accessToken.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Decode payload
      const payload = parts[1];
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
      const tokenPayload = JSON.parse(decoded);

      // Check expiration
      const now = Math.floor(Date.now() / 1000);
      if (!tokenPayload.exp || tokenPayload.exp < now) {
        return false;
      }

      // Check RT claim
      const allowedRtValues = ['ai', 'ag', 'at'];
      if (!tokenPayload.rt || !allowedRtValues.includes(tokenPayload.rt)) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }
}
